/**
 * Root Layout
 *
 * Provides all app-level context providers:
 * - Redux store
 * - SessionManager (auth)
 * - Theme provider
 * - Error boundary
 * - Toast container
 *
 * This replaces the provider structure from App.jsx and AppContent.jsx
 */
import React, { Suspense, useEffect, useRef } from 'react';
import { Provider as ReduxProvider } from 'react-redux';
import { Outlet } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/router-devtools';
import { ErrorBoundary } from 'react-error-boundary';

// Lazy-loaded: defers react-toastify JS (~30 KB) from the initial chunk.
// The toast() function still works synchronously; the container renders async.
const LazyToastContainer = React.lazy(() =>
  import('react-toastify').then((m) => ({ default: m.ToastContainer }))
);

import CloseIcon from '@mui/icons-material/Close';

import { cspNonce } from '../utils/cspNonce';
import { IconButton } from '@mui/material';

import {
  SessionManager,
  useAuth,
  storePermissions,
  clearPermissions,
  broadcastLogout,
  initAuthSync,
  consumePostLoginRedirect,
} from '@bb/session-manager';
import { default as reduxStore } from '../redux/store';
import * as oidcAuthUtil from '../auth/oidcAuthUtil';
import theme from '../styles/MaterialTheme';
import CustomThemeProvider from '../utils/CustomThemeProvider';
import { API_CONFIG_ENDPOINT } from '../endpoints.config';
import { ROUTE_PREFIX } from '../routes.config';
import { BBSpinner } from '../components/BBSpinner/BBSpinner';
import { SessionError } from '../components/SessionLoading';
import { ErrorFallback, LoadingFallback } from '../components';
import GlobalEventManager from '../GlobalEventManager';
import UserFlowManager from '../UserFlowManager';

// Initialize domain for OnlyOffice
oidcAuthUtil.changeDocumentDomainForOnlyOffice();

// Initialize cross-tab auth sync
initAuthSync();

/**
 * Toast close button
 */
const CustomCloseButton = ({ closeToast }) => (
  <IconButton
    aria-label="Close toast"
    size="large"
    sx={{ position: 'absolute', right: '12px' }}
    onClick={() => closeToast(true)}
  >
    <CloseIcon fontSize="large" />
  </IconButton>
);

/**
 * Inner content that requires SessionManager context
 */
function RootLayoutInner() {
  const { user, auth, config, setUser, setConfig } = useAuth();

  // Track initialization to prevent loops
  const userInitialized = useRef(false);
  const configInitialized = useRef(false);
  // Track if user was ever authenticated (to detect logout vs initial load)
  const wasAuthenticated = useRef(false);

  // Debug: Track component mount/unmount
  useEffect(() => {
    console.log('[RootLayout] Component mounted');
    return () => {
      console.log('[RootLayout] Component UNMOUNTING');
    };
  }, []);

  // User profile initialization
  useEffect(() => {
    if (
      auth.isAuthenticated &&
      !(user && 'permissions' in user) &&
      !userInitialized.current
    ) {
      userInitialized.current = true;
      wasAuthenticated.current = true;
      console.debug('[BB:RootLayout] User authenticated, fetching profile...');

      (async () => {
        try {
          let idToken = null;

          if (
            auth['getIdTokenClaims'] &&
            typeof auth['getIdTokenClaims'] === 'function'
          ) {
            const idTokenClaims = await auth['getIdTokenClaims']();
            idToken = idTokenClaims?.__raw;
          } else if (auth?.user?.id_token) {
            idToken = auth.user.id_token;
          } else if (config?.provider === 'gcp') {
            idToken = localStorage.getItem('bb.gcp.access_token');
          }

          const fetchedUser = await oidcAuthUtil.fetchCurrentBBUser(idToken);

          // Store permissions for beforeLoad access
          if (fetchedUser.permissions) {
            console.debug('[BB:RootLayout] Storing permissions from user profile:', [
              ...fetchedUser.permissions,
            ]);
            storePermissions(fetchedUser.permissions);
          }

          // Handle pending deep-link redirect (stored by onSigninCallback for OIDC
          // or onAuth0RedirectCallback for Auth0 when the user visited a protected
          // URL before being sent to login)
          const pendingRedirect = consumePostLoginRedirect();
          if (pendingRedirect) {
            console.debug(
              '[BB:RootLayout] Navigating to deep link after profile loaded:',
              pendingRedirect
            );
            window.location.href = pendingRedirect;
            return;
          }

          if (typeof setUser === 'function') {
            setUser(fetchedUser);
          }
        } catch (err) {
          console.error('Error fetching user profile:', err);
          userInitialized.current = false;
        }
      })();
    }

    // Only clear permissions and broadcast logout when transitioning from
    // authenticated -> unauthenticated (actual logout, not initial page load)
    if (!auth.isAuthenticated && wasAuthenticated.current) {
      console.debug(
        '[BB:RootLayout] User logged out, clearing permissions and broadcasting...'
      );
      clearPermissions();
      broadcastLogout('user_action');
      userInitialized.current = false;
      wasAuthenticated.current = false;
    }
  }, [auth.isAuthenticated, user, setUser]);

  // Config initialization effect
  useEffect(() => {
    if (
      auth.isAuthenticated &&
      config &&
      !('userFlowEnabled' in config) &&
      !configInitialized.current
    ) {
      configInitialized.current = true;

      (async () => {
        try {
          const authorizedConfig = await oidcAuthUtil.fetchConfig();
          if (typeof setConfig === 'function') {
            setConfig(authorizedConfig);
          }
        } catch (err) {
          console.error('Error fetching authorized config:', err);
          configInitialized.current = false;
        }
      })();
    }

    if (!auth.isAuthenticated) {
      configInitialized.current = false;
    }
  }, [auth.isAuthenticated, config, setConfig]);

  // Debug logging for auth state changes
  useEffect(() => {
    console.log('[RootLayout] Auth state:', {
      isLoading: auth.isLoading,
      isAuthenticated: auth.isAuthenticated,
      activeNavigator: auth['activeNavigator'],
      hasUser: !!auth.user,
      hasConfig: !!config,
    });
  }, [auth.isLoading, auth.isAuthenticated, auth['activeNavigator'], auth.user, config]);

  // Determine auth gate state
  // IMPORTANT: Never return early with a replacement component once the user has been
  // authenticated. Doing so unmounts the entire tree including GlobalEventManager's socket.
  // Instead, overlay loading/error states on top of the persistent layout.
  const isSilentRefresh = auth['activeNavigator'] === 'signinSilent';
  const isSigningOut = auth['activeNavigator'] === 'signoutRedirect';
  const isInitialLoading = auth.isLoading && !isSilentRefresh && !auth.isAuthenticated;
  const isSilentRefreshError = auth.error && isSilentRefresh;

  // Only show blocking UI states BEFORE the user has ever authenticated.
  // Once authenticated, the tree must stay mounted to preserve sockets/state.
  if (isSigningOut) {
    console.log('[RootLayout] Showing signout spinner');
    return <BBSpinner isCentered message="Signing you out..." />;
  }

  if (isInitialLoading) {
    console.log('[RootLayout] Showing auth loading spinner (initial load)');
    return <BBSpinner isCentered message="Authenticating..." />;
  }

  // Only show error screen for non-silent-refresh errors before first auth
  if (auth.error && !isSilentRefreshError && !auth.isAuthenticated) {
    // Check if this is a stale auth callback (e.g., duplicated tab or page reload
    // with already-consumed code/state params). Strip the params and reload — on
    // reload the auth provider will either find a valid session in localStorage
    // (from the tab that processed the callback successfully) or redirect to login.
    const currentUrl = new URL(window.location.href);
    if (currentUrl.searchParams.has('code') && currentUrl.searchParams.has('state')) {
      console.log(
        '[RootLayout] Auth callback failed (stale/duplicate), stripping params and reloading'
      );
      currentUrl.searchParams.delete('code');
      currentUrl.searchParams.delete('state');
      window.location.replace(currentUrl.pathname + currentUrl.search + currentUrl.hash);
      return <BBSpinner isCentered message="Authenticating..." />;
    }

    console.log('[RootLayout] Showing auth error:', auth.error.message);
    return <SessionError message={auth.error.message} />;
  }

  if (isSilentRefreshError) {
    // Silent refresh failed - log but don't unmount the tree.
    // The session hooks will handle retry/logout via backoff logic.
    console.warn(
      '[RootLayout] Silent refresh error (non-destructive):',
      auth.error.message
    );
  }

  if (!config) {
    console.log('[RootLayout] Showing config loading spinner');
    return <BBSpinner isCentered message="Loading configuration..." />;
  }

  return (
    <GlobalEventManager>
      <UserFlowManager>
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <Suspense fallback={<LoadingFallback />}>
            <Outlet />
          </Suspense>
          {/* Isolated Suspense: ToastContainer loads async without blocking the Outlet */}
          <Suspense fallback={null}>
            <LazyToastContainer
              position="bottom-center"
              autoClose={3000}
              pauseOnFocusLoss={false}
              closeButton={CustomCloseButton}
              nonce={cspNonce}
            />
          </Suspense>

          {/* Dev Tools - only in development */}
          {import.meta.env.DEV && <TanStackRouterDevtools position="bottom-right" />}
        </ErrorBoundary>
      </UserFlowManager>
    </GlobalEventManager>
  );
}

/**
 * Root Layout - top-level provider wrapper
 */
export default function RootLayout() {
  return (
    <ReduxProvider store={reduxStore}>
      <CustomThemeProvider theme={theme} productionPrefix="BBWeb-JSS-">
        <SessionManager
          configEndpoint={API_CONFIG_ENDPOINT}
          homeRoute={`${ROUTE_PREFIX}/home`}
          loginRoute={`${ROUTE_PREFIX}/login`}
          nonce={cspNonce}
          LoadingComponent={() => <BBSpinner isCentered message="Loading session..." />}
          ErrorComponent={SessionError}
        >
          <RootLayoutInner />
        </SessionManager>
      </CustomThemeProvider>
    </ReduxProvider>
  );
}
